/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, HostListener, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ContentNodeSelectorComponent, ContentNodeSelectorComponentData, NodeAction } from '@alfresco/adf-content-services';
import { NotificationService } from '@alfresco/adf-core';
import { Node } from '@alfresco/js-api';
import { PageHeaderComponent, PageStateComponent } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { DmsMetadataService, DmsOption } from '../upload/dms-metadata.service';
import { WorkflowService, WorkflowTemplate } from '../workflow/workflow.service';
import { FormFillComponent } from './form-fill.component';
import { FormPaperComponent, rememberPaper } from './form-paper.component';
import { FormPdfDialogComponent } from './form-pdf-dialog.component';
import { MAX_PAPER_BYTES, MAX_PAPER_PAGES, PaperError, readPaper, sampleValues } from './forms.paper';
import { FormDraftHandover, handedDraft } from './forms.draft';
import {
  ACCENTS,
  cleanKey,
  columnFormulaProblem,
  conditionProblem,
  ConditionOp,
  definitionProblems,
  editedVersion,
  emptyValues,
  FIELD_TYPES,
  isWideType,
  FormColumn,
  FormColumnType,
  FormCondition,
  FormDefinition,
  FormField,
  FormFieldType,
  FormulaProblem,
  formulaProblem,
  FormStyle,
  hasOptions,
  isNumericColumn,
  isNumericField,
  MAX_LOGO_BYTES,
  METADATA_TARGETS,
  newField,
  newForm,
  newSection,
  pdfName,
  allFields,
  withStatus
} from './forms.model';

/** Something a condition can be put on: a field or a whole section. */
interface Conditional {
  showIf?: FormCondition;
}

/** A name a formula can use: what the chip says, and what clicking it types. */
interface FormulaName {
  insert: string;
  label: string;
}
import { FormPrintError, FormsService } from './forms.service';

type Selection = { kind: 'form' } | { kind: 'section'; section: number } | { kind: 'field'; section: number; field: number };

/**
 * The form builder: a palette of field types, the form's sections with their
 * fields (dragged in from the palette, dragged to reorder or between
 * sections), and the properties of whatever is selected. The preview tab is
 * the fill-in page itself, and the PDF button prints the form blank, as it
 * would go on paper.
 */
@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DragDropModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    FormFillComponent,
    FormPaperComponent,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss']
})
export class FormBuilderComponent implements OnInit {
  private readonly forms = inject(FormsService);
  private readonly metadata = inject(DmsMetadataService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly workflow = inject(WorkflowService);

  readonly fieldTypes = FIELD_TYPES;
  readonly isWideType = isWideType;

  model: FormDefinition = newForm();
  /** The saved file's text, to tell whether anything changed. */
  private saved = '';
  /** A frozen copy the preview tab renders, so typing in the builder does not reset what was typed in the preview. */
  previewModel: FormDefinition = this.model;

  readonly loading = signal(false);
  readonly failed = signal('');
  readonly working = signal<'save' | 'pdf' | 'paper' | null>(null);
  readonly tab = signal<'design' | 'paper' | 'preview'>('design');
  selection: Selection = { kind: 'form' };
  /** Set when the form on screen is a draft the AI read from an existing one, not yet saved. */
  readonly drafted = signal<FormDraftHandover | null>(null);

  documentTypes: DmsOption[] = [];
  statuses: DmsOption[] = [];
  confidentialities: DmsOption[] = [];
  /** The flows a filed form can start: every enabled, published one in the catalogue. */
  flows: WorkflowTemplate[] = [];
  flowsLoaded = false;

  ngOnInit(): void {
    this.workflow.ensureTemplatesLoaded().subscribe({
      next: () => {
        this.flows = this.workflow.getStartableTemplates();
        this.flowsLoaded = true;
      },
      error: () => (this.flowsLoaded = true)
    });

    this.metadata.schema().subscribe({
      next: (schema) => {
        this.documentTypes = schema.fields['dms:documentType']?.options ?? [];
        this.statuses = schema.fields['dms:status']?.options ?? [];
        this.confidentialities = schema.fields['dms:confidentiality']?.options ?? [];
      },
      error: () => undefined
    });

    const id = this.route.snapshot.paramMap.get('id');
    if (!id || id === 'new') {
      const handover = handedDraft(history.state);
      this.model = handover?.form ?? newForm();
      this.drafted.set(handover);
      this.saved = '';
      return;
    }
    this.loading.set(true);
    this.forms.get(id).subscribe({
      next: (form) => {
        this.loading.set(false);
        this.model = form;
        this.saved = this.snapshot();
      },
      error: (error) => {
        this.loading.set(false);
        this.failed.set(error?.message ?? String(error));
      }
    });
  }

  /** Leaving with unsaved changes asks first. */
  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty) {
      event.preventDefault();
    }
  }

  get dirty(): boolean {
    return this.snapshot() !== this.saved;
  }

  get problems(): string[] {
    return definitionProblems(this.model);
  }

  get selectedField(): FormField | null {
    const s = this.selection;
    return s.kind === 'field' ? (this.model.sections[s.section]?.fields[s.field] ?? null) : null;
  }

  get selectedSectionIndex(): number {
    return this.selection.kind === 'form' ? -1 : this.selection.section;
  }

  typeIcon(type: FormFieldType): string {
    return FIELD_TYPES.find((t) => t.type === type)?.icon ?? 'help';
  }

  typeLabel(type: FormFieldType): string {
    return this.translate.instant(`DMS.FORMS.TYPE_${type.toUpperCase()}`);
  }

  // ── tabs ──────────────────────────────────────────────────────────────

  showTab(tab: 'design' | 'paper' | 'preview'): void {
    if (tab === 'preview') {
      this.previewModel = JSON.parse(JSON.stringify(this.model));
    }
    this.tab.set(tab);
  }

  // ── selecting ─────────────────────────────────────────────────────────

  selectForm(): void {
    this.selection = { kind: 'form' };
  }

  selectSection(section: number): void {
    this.selection = { kind: 'section', section };
  }

  selectField(section: number, field: number, event?: Event): void {
    event?.stopPropagation();
    this.selection = { kind: 'field', section, field };
  }

  isSelected(section: number, field?: number): boolean {
    const s = this.selection;
    return field === undefined ? s.kind === 'section' && s.section === section : s.kind === 'field' && s.section === section && s.field === field;
  }

  // ── sections ──────────────────────────────────────────────────────────

  addSection(): void {
    this.model.sections.push(newSection(this.model.sections.length + 1));
    this.selectSection(this.model.sections.length - 1);
  }

  moveSection(index: number, by: number): void {
    const to = index + by;
    if (to < 0 || to >= this.model.sections.length) {
      return;
    }
    moveItemInArray(this.model.sections, index, to);
    this.selectSection(to);
  }

  removeSection(index: number): void {
    if (this.model.sections.length === 1) {
      return;
    }
    this.model.sections.splice(index, 1);
    this.selectForm();
  }

  // ── fields ────────────────────────────────────────────────────────────

  /** Clicking a palette entry adds the field to the selected section, or the last one. */
  addField(type: FormFieldType): void {
    const section = this.selectedSectionIndex >= 0 ? this.selectedSectionIndex : this.model.sections.length - 1;
    const fields = this.model.sections[section].fields;
    const at = this.selection.kind === 'field' ? this.selection.field + 1 : fields.length;
    fields.splice(at, 0, newField(type, this.model, this.typeLabel(type)));
    this.selectField(section, at);
  }

  /** A palette entry dropped into a section, or a field moved within or between sections. */
  drop(event: CdkDragDrop<FormField[]>, section: number): void {
    if (event.previousContainer.id === 'aca-frb-palette') {
      const type = event.item.data as FormFieldType;
      event.container.data.splice(event.currentIndex, 0, newField(type, this.model, this.typeLabel(type)));
    } else if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
    this.selectField(section, event.currentIndex);
  }

  readonly noDropOnPalette = (): boolean => false;

  sectionListIds(): string[] {
    return this.model.sections.map((_, i) => `aca-frb-section-${i}`);
  }

  duplicateField(): void {
    const s = this.selection;
    const field = this.selectedField;
    if (s.kind !== 'field' || !field) {
      return;
    }
    const copy: FormField = JSON.parse(JSON.stringify(field));
    copy.key = newField(field.type, this.model, '').key;
    // two fields printed in the same blank would overprint each other
    delete copy.places;
    this.model.sections[s.section].fields.splice(s.field + 1, 0, copy);
    this.selectField(s.section, s.field + 1);
  }

  removeField(): void {
    const s = this.selection;
    if (s.kind !== 'field') {
      return;
    }
    this.model.sections[s.section].fields.splice(s.field, 1);
    this.selectSection(s.section);
  }

  /** A key has to stay a usable, unique name: tidy it when the person leaves the box. */
  tidyKey(field: FormField): void {
    let key = cleanKey(field.key);
    const others = new Set(
      allFields(this.model)
        .filter((f) => f !== field)
        .map((f) => f.key)
    );
    const base = key;
    for (let n = 2; others.has(key); n++) {
      key = `${base}${n}`;
    }
    field.key = key;
  }

  hasOptions(field: FormField): boolean {
    return hasOptions(field.type);
  }

  optionsText(field: FormField): string {
    return (field.options ?? []).join('\n');
  }

  setOptions(field: FormField, text: string): void {
    field.options = text
      .split('\n')
      .map((o) => o.trim())
      .filter((o, i, all) => o && all.indexOf(o) === i);
  }

  addColumn(field: FormField): void {
    const keys = new Set((field.columns ?? []).map((c) => c.key));
    let n = (field.columns ?? []).length + 1;
    while (keys.has(`col${n}`)) {
      n++;
    }
    field.columns = [...(field.columns ?? []), { key: `col${n}`, label: '', type: 'text' }];
  }

  removeColumn(field: FormField, index: number): void {
    field.columns = (field.columns ?? []).filter((_, i) => i !== index);
  }

  readonly columnTypes: FormColumnType[] = ['text', 'number', 'money', 'date', 'calc'];

  /** The dms: properties this field's type can fill. */
  metadataTargets(field: FormField): string[] {
    return METADATA_TARGETS.filter((t) => t.types.includes(field.type)).map((t) => t.property);
  }

  // ── calculations ──────────────────────────────────────────────────────

  /** The names a calc field may use, to click into its formula: shown by label, inserted by key. */
  formulaNames(field: FormField): FormulaName[] {
    const names: FormulaName[] = [];
    for (const other of allFields(this.model)) {
      if (other === field) {
        continue;
      }
      if (isNumericField(other)) {
        names.push({ insert: other.key, label: other.label || other.key });
      }
      if (other.type === 'table') {
        const table = other.label || other.key;
        names.push(
          ...(other.columns ?? []).filter(isNumericColumn).map((c) => ({
            insert: `${other.key}.${c.key}`,
            label: this.translate.instant('DMS.FORMS.NAME_TOTAL', { table, column: c.label || c.key })
          })),
          { insert: `count(${other.key})`, label: this.translate.instant('DMS.FORMS.NAME_COUNT', { table }) }
        );
      }
    }
    return names;
  }

  /** The other number columns a calc column may use. */
  columnNames(table: FormField, column: FormColumn): FormulaName[] {
    return (table.columns ?? []).filter((c) => c !== column && isNumericColumn(c)).map((c) => ({ insert: c.key, label: c.label || c.key }));
  }

  /** The chips are built afresh on every check; without this they are replaced between a press and its release, and the click is lost. */
  readonly trackName = (_: number, name: FormulaName): string => name.insert;

  insertName(target: { formula?: string }, name: string): void {
    const formula = (target.formula ?? '').trimEnd();
    target.formula = formula && !/[+\-*/(,]$/.test(formula) ? `${formula} + ${name}` : `${formula}${formula ? ' ' : ''}${name}`;
  }

  formulaProblem(field: FormField): string {
    return this.problemWords(field.formula, formulaProblem(this.model, field));
  }

  columnFormulaProblem(table: FormField, column: FormColumn): string {
    return this.problemWords(column.formula, columnFormulaProblem(table, column));
  }

  private problemWords(formula: string | undefined, problem: FormulaProblem | null): string {
    if (!problem) {
      return '';
    }
    // a formula not written yet is not "wrong", just missing
    const code = problem.code === 'NOT_ARITHMETIC' && !formula?.trim() ? 'EMPTY' : problem.code;
    return this.translate.instant(`DMS.FORMS.FORMULA_${code}`, { name: problem.name ?? '' });
  }

  /** A column turned into a calc column starts as money, with an empty formula to fill in. */
  columnTypeChanged(column: FormColumn): void {
    if (column.type === 'calc') {
      column.formula ??= '';
      column.format ??= 'money';
    } else {
      delete column.formula;
      delete column.format;
    }
  }

  // ── conditions ────────────────────────────────────────────────────────

  /** Fields a condition can look at: any that takes a value, except tables and the field itself. */
  conditionSources(owner?: FormField): FormField[] {
    return allFields(this.model).filter((f) => f !== owner && f.type !== 'paragraph' && f.type !== 'table');
  }

  conditionSource(condition: FormCondition): FormField | undefined {
    return allFields(this.model).find((f) => f.key === condition.field);
  }

  /** The tests that make sense for the field looked at. */
  conditionOps(condition: FormCondition): ConditionOp[] {
    const source = this.conditionSource(condition);
    switch (source?.type) {
      case 'checkbox':
      case 'signature':
      case 'address':
        return ['filled', 'empty'];
      case 'checkboxes':
        return ['includes', 'filled', 'empty'];
      case 'select':
      case 'radio':
        return ['equals', 'notEquals', 'filled', 'empty'];
      case 'number':
      case 'money':
      case 'calc':
        return ['greater', 'less', 'equals', 'notEquals', 'filled', 'empty'];
      default:
        return ['filled', 'empty', 'equals', 'notEquals'];
    }
  }

  /** A tick box reads "is ticked", a signature "is signed", not "is filled in". */
  opLabel(condition: FormCondition, op: ConditionOp): string {
    const source = this.conditionSource(condition)?.type;
    let key = op.replace(/[A-Z]/g, (c) => '_' + c).toUpperCase();
    if (source === 'checkbox') {
      key = op === 'filled' ? 'TICKED' : 'NOT_TICKED';
    } else if (source === 'signature') {
      key = op === 'filled' ? 'SIGNED' : 'NOT_SIGNED';
    }
    return this.translate.instant(`DMS.FORMS.OP_${key}`);
  }

  needsValue(condition: FormCondition): boolean {
    return condition.op !== 'filled' && condition.op !== 'empty';
  }

  setConditional(target: Conditional, on: boolean, owner?: FormField): void {
    if (!on) {
      delete target.showIf;
      return;
    }
    const first = this.conditionSources(owner)[0];
    target.showIf = { field: first?.key ?? '', op: 'filled' };
    if (first) {
      this.conditionFieldChanged(target.showIf);
    }
  }

  /** Another field to look at: keep the test if it still fits, and start the value afresh. */
  conditionFieldChanged(condition: FormCondition): void {
    const ops = this.conditionOps(condition);
    if (!ops.includes(condition.op)) {
      condition.op = ops[0];
    }
    const options = this.conditionSource(condition)?.options ?? [];
    condition.value = this.needsValue(condition) && options.length ? options[0] : '';
  }

  conditionProblem(condition: FormCondition, owner?: FormField): string {
    const problem = conditionProblem(this.model, condition, owner);
    return problem ? this.translate.instant(`DMS.FORMS.CONDITION_${problem}`) : '';
  }

  /** "Shown when ประเภท = อื่น ๆ", for the canvas. */
  conditionSummary(condition: FormCondition): string {
    const source = this.conditionSource(condition);
    const value = this.needsValue(condition) ? ` ${condition.value ?? ''}` : '';
    return this.translate.instant('DMS.FORMS.SHOWN_WHEN', {
      condition: `${source?.label || condition.field} ${this.opLabel(condition, condition.op)}${value}`
    });
  }

  // ── appearance ────────────────────────────────────────────────────────

  readonly accents = ACCENTS;

  get look(): FormStyle {
    this.model.style ??= {};
    return this.model.style;
  }

  pickLogo(input: HTMLInputElement): void {
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      this.notification.showError(this.translate.instant('DMS.FORMS.LOGO_WRONG_TYPE'));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      this.notification.showError(this.translate.instant('DMS.FORMS.LOGO_TOO_BIG', { size: Math.round(MAX_LOGO_BYTES / 1000) }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => (this.look.logo = String(reader.result));
    reader.readAsDataURL(file);
  }

  removeLogo(): void {
    delete this.look.logo;
  }

  // ── the organisation's own paper ──────────────────────────────────────

  /** A PDF, or a photo or scan of the paper, to print the values onto. Its places are kept when the paper is replaced. */
  async pickPaper(input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    this.working.set('paper');
    try {
      const paper = await readPaper(file);
      const { nodeId, name } = await firstValueFrom(this.forms.uploadPaper(paper.pdf, file.name));
      rememberPaper(nodeId, new Uint8Array(await paper.pdf.arrayBuffer()));
      this.model.paper = { nodeId, name, pages: paper.pages, marks: this.model.paper?.marks ?? [] };
      this.showTab('paper');
    } catch (error) {
      const problem = error instanceof PaperError ? error.problem : null;
      const key = problem
        ? { type: 'PAPER_WRONG_TYPE', size: 'PAPER_TOO_BIG', pages: 'PAPER_TOO_MANY_PAGES', unreadable: 'PAPER_UNREADABLE' }[problem]
        : 'PAPER_UPLOAD_FAILED';
      this.notification.showError(this.translate.instant(`DMS.FORMS.${key}`, { pages: MAX_PAPER_PAGES, size: MAX_PAPER_BYTES / 1024 / 1024 }));
    } finally {
      this.working.set(null);
    }
  }

  /** Back to the standard layout: the places go too, so nothing is left pointing at a paper that is not there. */
  removePaper(): void {
    delete this.model.paper;
    allFields(this.model).forEach((f) => delete f.places);
    if (this.tab() === 'paper') {
      this.tab.set('design');
    }
  }

  // ── form settings ─────────────────────────────────────────────────────

  get signaturesText(): string {
    return (this.model.footer?.signatures ?? []).join('\n');
  }

  set signaturesText(text: string) {
    this.model.footer = {
      ...(this.model.footer ?? { signatures: [] }),
      signatures: text
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
    };
  }

  get tagsText(): string {
    return (this.model.output?.tags ?? []).join(', ');
  }

  set tagsText(text: string) {
    this.output.tags = text
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }

  /** Remembers the flow's name with its id, so the form can say which flow it meant should it go. */
  flowChanged(id: string): void {
    if (id) {
      this.output.workflowId = id;
      this.output.workflowName = this.flows.find((t) => t.id === id)?.name ?? '';
    } else {
      delete this.output.workflowId;
      delete this.output.workflowName;
    }
  }

  /** The chosen flow has been switched off, unpublished or deleted since. */
  get flowGone(): boolean {
    return this.flowsLoaded && !!this.output.workflowId && !this.flows.some((t) => t.id === this.output.workflowId);
  }

  get output() {
    this.model.output ??= {};
    return this.model.output;
  }

  get header() {
    this.model.header ??= {};
    return this.model.header;
  }

  get footer() {
    this.model.footer ??= { signatures: [] };
    return this.model.footer;
  }

  pickFolder(): void {
    const select = new Subject<Node[]>();
    const data: ContentNodeSelectorComponentData = {
      title: 'DMS.FORMS.PICK_FOLDER',
      actionName: NodeAction.CHOOSE,
      currentFolderId: this.output.folderId || '-my-',
      selectionMode: 'single',
      where: '(isFolder=true)',
      isSelectionValid: (node: Node) => node.isFolder,
      select
    };
    const picker = this.dialog.open(ContentNodeSelectorComponent, { data, panelClass: 'adf-content-node-selector-dialog', width: '630px' });
    select.subscribe((nodes) => {
      const folder = nodes?.[0];
      if (folder) {
        this.output.folderId = folder.id;
        this.output.folderName = folder.name;
      }
      picker.close();
    });
  }

  clearFolder(): void {
    delete this.output.folderId;
    delete this.output.folderName;
  }

  // ── saving and printing ───────────────────────────────────────────────

  save(publish = false): void {
    if (publish && this.problems.length) {
      this.notification.showError(this.translate.instant('DMS.FORMS.CANNOT_PUBLISH'));
      return;
    }
    if (!this.model.title.trim()) {
      this.notification.showError(this.translate.instant('DMS.FORMS.PROBLEM_NO_TITLE'));
      return;
    }
    let next: FormDefinition = { ...this.model };
    if (this.dirty && this.model.id) {
      next.version = editedVersion(this.model);
    }
    if (publish) {
      next = withStatus(next, 'published');
    }
    this.working.set('save');
    this.forms.save(next).subscribe({
      next: (saved) => {
        this.working.set(null);
        const isNew = !this.model.id;
        this.model = saved;
        this.saved = this.snapshot();
        this.notification.showInfo(this.translate.instant(publish ? 'DMS.FORMS.PUBLISHED' : 'DMS.FORMS.SAVED', { title: saved.title }));
        if (isNew) {
          void this.router.navigate(['/forms', saved.id, 'edit'], { replaceUrl: true });
        }
      },
      error: () => {
        this.working.set(null);
        this.notification.showError(this.translate.instant('DMS.FORMS.SAVE_FAILED'));
      }
    });
  }

  /** The form as it stands, printed blank; on its paper, with a sample in every place to check where each lands. */
  printBlank(): void {
    this.working.set('pdf');
    this.forms.preview(this.model, this.model.paper ? sampleValues(this.model) : emptyValues(this.model)).subscribe({
      next: ({ pdf, reference }) => {
        this.working.set(null);
        this.dialog.open(FormPdfDialogComponent, {
          data: { pdf, fileName: pdfName(this.model, reference || 'preview') },
          width: '920px',
          maxWidth: '96vw',
          height: '90vh',
          panelClass: 'aca-frmpdf-panel'
        });
      },
      error: (reason: FormPrintError) => {
        this.working.set(null);
        this.notification.showError(
          this.translate.instant(reason?.kind === 'engine' ? 'DMS.FORMS.ENGINE_DOWN' : 'DMS.FORMS.SUBMIT_FAILED', { message: reason?.message ?? '' })
        );
      }
    });
  }

  private snapshot(): string {
    // what the form says, not where it is stored or who saved it last
    const content: FormDefinition = { ...this.model };
    delete content.id;
    delete content.updatedAt;
    delete content.updatedBy;
    return JSON.stringify(content);
  }
}
